/**
 * useSelectionHandlers - Event Handlers for Selection Management
 *
 * Single responsibility: Providing event handlers for user interactions.
 * Handles team selection, prop selection, tiebreaker input, clear, and quick pick.
 */

import { useCallback } from 'react';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import {
  QUESTION_TYPES,
  OPTION_SELECTION_QUESTION_TYPES,
  OVER_UNDER_QUESTION_TYPES,
} from '@/constants/question-types';
import { getQuestionId, getOptionId } from '@/utils/id-helpers';
import type { SelectionState } from './useSelectionState';
import { findOverUnderOption } from '@/utils/over-under-option';

/**
 * Creates event handlers for selection management
 *
 * @param setSelections - Function to update selections state
 * @param pickemDetails - Pickem details for quick pick logic
 * @param selections - Current selection state to check for existing selections
 * @returns Object with all handler functions
 */
export function useSelectionHandlers(
  setSelections: React.Dispatch<React.SetStateAction<SelectionState>>,
  pickemDetails: PickemDetailResponse | undefined,
  selections: SelectionState
) {
  /**
   * Handle team or player selection
   */
  const handleTeamSelect = useCallback(
    (questionId: string, teamId: string | undefined) => {
      setSelections(prev => {
        if (teamId) {
          return {
            ...prev,
            teams: { ...prev.teams, [questionId]: teamId },
          };
        }

        // Remove selection if teamId is undefined
        const { [questionId]: _removed, ...rest } = prev.teams;
        return {
          ...prev,
          teams: rest,
        };
      });
    },
    [setSelections]
  );

  /**
   * Handle prop question answer
   */
  const handlePropSelect = useCallback(
    (questionId: string, answer: 'yes' | 'no') => {
      setSelections(prev => ({
        ...prev,
        props: { ...prev.props, [questionId]: answer },
      }));
    },
    [setSelections]
  );

  /**
   * Handle over/under question answer
   */
  const handleOverUnderSelect = useCallback(
    (questionId: string, answer: 'over' | 'under' | undefined) => {
      setSelections(prev => {
        if (answer) {
          return {
            ...prev,
            overUnder: { ...prev.overUnder, [questionId]: answer },
          };
        }

        // Remove selection if answer is undefined (deselection)
        const { [questionId]: _removed, ...rest } = prev.overUnder;
        return {
          ...prev,
          overUnder: rest,
        };
      });
    },
    [setSelections]
  );

  /**
   * Handle tiebreaker input change
   */
  const handleTieBreakerChange = useCallback(
    (value: string) => {
      setSelections(prev => ({
        ...prev,
        tieBreaker: value,
      }));
    },
    [setSelections]
  );

  /**
   * Clear all selections
   */
  const clearSelections = useCallback(() => {
    setSelections(() => ({
      teams: {},
      props: {},
      overUnder: {},
      tieBreaker: '',
    }));
  }, [setSelections]);

  /**
   * Randomly select answers for all questions (Quick Pick)
   * Only selects for questions that haven't been selected yet
   */
  const quickPick = useCallback(() => {
    if (!pickemDetails?.pickem.questions) return;

    // Start with existing selections
    const teamSelections: Record<string, string> = { ...selections.teams };
    const propSelections: Record<string, 'yes' | 'no'> = { ...selections.props };
    const overUnderSelections: Record<string, 'over' | 'under'> = { ...selections.overUnder };

    pickemDetails.pickem.questions.forEach(question => {
      const questionId = getQuestionId(question);

      // Option picks — team / player / custom / leader / moneyline / spread —
      // all stored in selections.teams (the bucket holds an option_id keyed by
      // question_id, regardless of the underlying type; DKPLAT-354, DKPLAT-500).
      if (
        OPTION_SELECTION_QUESTION_TYPES.includes(question.question_type) &&
        !teamSelections[questionId] &&
        question.options.length > 0
      ) {
        const randomOption = question.options[Math.floor(Math.random() * question.options.length)];
        teamSelections[questionId] = getOptionId(randomOption);
      }
      // Prop — yes/no
      else if (question.question_type === QUESTION_TYPES.PROP && !propSelections[questionId]) {
        propSelections[questionId] = Math.random() > 0.5 ? 'yes' : 'no';
      }
      // Over/under (incl. match-level Total)
      else if (
        OVER_UNDER_QUESTION_TYPES.includes(question.question_type) &&
        !overUnderSelections[questionId]
      ) {
        overUnderSelections[questionId] = Math.random() > 0.5 ? 'over' : 'under';
      }
      // Tiebreaker is intentionally excluded — left for the user to
      // enter manually since it's a meaningful prediction, not a guess.
    });

    setSelections({
      teams: teamSelections,
      props: propSelections,
      overUnder: overUnderSelections,
      tieBreaker: selections.tieBreaker,
    });
  }, [pickemDetails, setSelections, selections]);

  const favPick = useCallback(() => {
    if (!pickemDetails?.pickem.questions) return;

    // Start with existing selections
    const teamSelections: Record<string, string> = { ...selections.teams };
    const propSelections: Record<string, 'yes' | 'no'> = { ...selections.props };
    const overUnderSelections: Record<string, 'over' | 'under'> = { ...selections.overUnder };

    pickemDetails.pickem.questions.forEach(question => {
      const questionId = getQuestionId(question);

      // Option picks — team / player / custom / leader / moneyline / spread —
      // all stored in selections.teams.
      if (
        OPTION_SELECTION_QUESTION_TYPES.includes(question.question_type) &&
        !teamSelections[questionId] &&
        question.options.length > 0
      ) {
        // Find option with highest popularity_pct (defaults to first
        // option when all options have null/0 popularity). For leader
        // questions this is "the candidate most users picked" — same
        // semantics as a player/team question.
        const mostPopularOption = question.options.reduce((max, option) => {
          const maxPct = max.popularity_pct ?? 0;
          const optionPct = option.popularity_pct ?? 0;
          return optionPct > maxPct ? option : max;
        }, question.options[0]);
        teamSelections[questionId] = getOptionId(mostPopularOption);
      }
      // Handle prop questions - only if not already selected
      else if (question.question_type === QUESTION_TYPES.PROP && !propSelections[questionId]) {
        // Find yes and no options and select the one with higher popularity_pct
        const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
        const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));
        const yesPct = yesOption?.popularity_pct ?? 0;
        const noPct = noOption?.popularity_pct ?? 0;
        propSelections[questionId] = yesPct >= noPct ? 'yes' : 'no';
      }
      // Handle over/under questions (incl. Total) - only if not already selected
      else if (
        OVER_UNDER_QUESTION_TYPES.includes(question.question_type) &&
        !overUnderSelections[questionId]
      ) {
        // Find over and under options and select the one with higher popularity_pct
        const overOption = findOverUnderOption(question, 'over');
        const underOption = findOverUnderOption(question, 'under');
        const overPct = overOption?.popularity_pct ?? 0;
        const underPct = underOption?.popularity_pct ?? 0;
        overUnderSelections[questionId] = overPct >= underPct ? 'over' : 'under';
      }
    });

    setSelections({
      teams: teamSelections,
      props: propSelections,
      overUnder: overUnderSelections,
      tieBreaker: selections.tieBreaker,
    });
  }, [pickemDetails, setSelections, selections]);

  return {
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
  };
}
